import { useContext } from "react";

import type { UseQueryResult } from "react-query";

import type { FunctionalityHistory } from "api/models/FunctionalityHistory";
import type { Reports } from "api/models/Reports";
import { CurrentPatientContext } from "utils/contexts";

export type ChartType = "pain" | "feeling" | "physical-function";

interface UseChartDataProps {
  type: ChartType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  feelingResponse?: UseQueryResult<any[], Error>;
  painResponse?: UseQueryResult<Reports[], Error>;
  physicalFunctionResponse?: UseQueryResult<FunctionalityHistory, Error>;
}

const toNumber = (val: string | number): number => (typeof val === "string" ? parseInt(val, 10) : val);

export const useChartData = ({ type, feelingResponse, painResponse, physicalFunctionResponse }: UseChartDataProps) => {
  const { patient } = useContext(CurrentPatientContext);

  let currentWeek = 0;
  let currentValue = 0;
  const graphData: { [key: string]: number }[] = [];
  let progress: (string | number)[] = [];
  let isError: boolean | undefined;
  let isLoading: boolean | undefined;

  const calculateProgress = (start?: number, end?: number) => {
    if (start === undefined || end === undefined) return [0, 0];
    const difference = end - start;
    if (start === 0) {
      return [difference, difference === 0 ? 0 : ((end / 1) * 100).toFixed(0)];
    }
    const percentageDifference = ((end / start - 1) * 100).toFixed(0);
    return [difference, percentageDifference];
  };

  const handlePainGraphData = () => {
    if (
      (patient?.specific_joint_pain || patient?.specific_joint_pain === 0) &&
      // eslint-disable-next-line no-prototype-builtins
      painResponse?.data?.find(week => week.hasOwnProperty("general_feeling"))
    ) {
      graphData.push({ week: 0, specific_joint_pain: patient?.specific_joint_pain });
    }

    painResponse?.data?.forEach(dataPoint => {
      if (typeof dataPoint.specific_joint_pain === "number") {
        graphData.push({ week: dataPoint.week, specific_joint_pain: dataPoint.specific_joint_pain });
      }
    });

    const startValue = patient?.specific_joint_pain;
    currentValue = graphData[0]?.specific_joint_pain ?? 0;
    currentWeek = graphData[0]?.week ?? 0;
    progress = calculateProgress(startValue, currentValue);
    isError = painResponse?.isError;
    isLoading = painResponse?.isLoading;
  };

  const handlePhysicalFunctionData = () => {
    physicalFunctionResponse?.data?.time_series.forEach(dataPoint => {
      if (typeof dataPoint.repetitions === "number") {
        graphData.push({ week: dataPoint.week, repetitions: dataPoint.repetitions });
      }
    });

    const startValue = graphData[0]?.repetitions;
    currentValue = graphData[graphData.length - 1]?.repetitions ?? 0;
    currentWeek = graphData[graphData.length - 1]?.week ?? 0;
    progress = calculateProgress(startValue, currentValue);
    isError = physicalFunctionResponse?.isError;
    isLoading = physicalFunctionResponse?.isLoading;
  };

  const handleFeelingData = () => {
    feelingResponse?.data?.forEach(dataPoint => {
      if (typeof dataPoint.general_feeling === "number") {
        graphData.push({ week: dataPoint.week, general_feeling: dataPoint.general_feeling });
      }
    });

    const startValue = graphData[graphData.length - 1]?.general_feeling;
    currentValue = graphData[0]?.general_feeling ?? 0;
    currentWeek = graphData[0]?.week ?? 0;
    progress = calculateProgress(startValue, currentValue);
    isError = feelingResponse?.isError;
    isLoading = feelingResponse?.isLoading;
  };

  if (type === "pain") {
    handlePainGraphData();
  } else if (type === "feeling") {
    handleFeelingData();
  } else {
    handlePhysicalFunctionData();
  }

  const generateSinceStart = () => {
    if (graphData.length === 0) return "-";
    if (progress[1] === "Infinity") return "-";
    const val = toNumber(progress[1]);
    return val > 0 ? `+${val}%` : `${val}%`;
  };

  return {
    graphData,
    currentValue,
    currentWeek,
    progress,
    isError,
    isLoading,
    generateSinceStart,
    patient,
  };
};
